import React, { useContext, useState, useRef } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";

import { ColorValues } from "manifest-components/src/Colors";
import { ReactSelectOptions } from "manifest-components/src/types";
import { StyledTile } from "manifest-components/src/Tiles/Tile";
import { ReactComponent as Pencil } from "manifest-components/src/Images/icons/Pencil.svg";
import { Button } from "manifest-components/src/Button";
import TransferInstruction, {
  TransferActions,
  TransferCancelReason,
  TransferUserInputReason,
  TransferInitiationMethod,
} from "manifest-components/src/classes/transferInstruction";
import { DestinationTransferOptions } from "manifest-components/src/classes/transferOptions";
import { TransferStatusTypes } from "manifest-components/src/classes/transfer";
import CoreAPI from "manifest-components/src/api/coreAPI";
import APITransferInstruction from "manifest-components/src/api/APITransferInstruction";
import { Input } from "manifest-components/src/Inputs/Input";
import useDidMountEffect from "manifest-components/src/hooks/useDidMountEffect";
import { ResolvedPromise } from "manifest-components/src/utils/resolvedPromise";
import { ZohoBookingPopup } from "manifest-components/src/Popup";
import User from "manifest-components/src/classes/user";
import UserDetails from "manifest-components/src/classes/userDetails";
import axios from "axios";
import CheckboxMenuItem from "./menuItems/CheckboxMenuItem";
import { DevelopmentEnvironmentContext } from "../../context/DevelopmentEnvironment/developmentEnvironmentProvider";
import { AppContext } from "../../context/App/appProvider";
import LocationStateDebugger from "./LocationStateDebugger";
import API from "../../utils/api";
import { RoutePaths, route, useTypedLocation, useTypedNavigate } from "../../routes/RoutePaths";
import Session from "../../utils/session";
import { envVariables, isDevelopmentEnvironment, isLocalEnvironment } from "../../env";
import { generateZohoBookingIframe } from "../../utils/zohoBooking";

const CircleContainer = styled.div`
  position: fixed;
  z-index: 999;
  left: 30px;
  bottom: 10px;
  height: 60px;
  width: 60px;
  border-radius: 50%;
  background: rgb(157, 198, 189);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgb(149, 190, 181);
  }

  svg {
    width: 50%;
    height: 50%;

    path {
      fill: #103930;
    }
  }

  @media (min-height: 741px) {
    bottom: 50px;
  }
`;

const StyledCalendly = styled.div`
  height: 100dvh;

  iframe {
    height: 100%;
    width: 100%;
  }
  @media (min-width: 950px) {
    height: calc(100dvh - 160px);
  }
`;

const DeveloperTools = () => {
  const { state, toggleMenu } = useContext(DevelopmentEnvironmentContext);
  if (!isDevelopmentEnvironment()) return null;

  return (
    <>
      <CircleContainer id="dev-tool-pen" role="button" onClick={() => toggleMenu()}>
        <Pencil />
      </CircleContainer>
      {state.showMenu ? <Menu /> : null}
      {state.showLocationState ? <LocationStateDebugger /> : null}
    </>
  );
};

/**
 * STYLING FOR MENU COMPONENT
 */

const MyTile = styled(StyledTile)`
  position: fixed;
  z-index: 1000;
  bottom: 90px;
  left: 30px;
  width: 340px;
  cursor: auto;
  display: block;
  zoom: 80%;

  @media (min-height: 741px) {
    bottom: 130px;
  }
`;

const MenuContainer = styled.ul`
  width: 100%;
  height: 100%;
  list-style: none;
  padding: 0px;
  margin: 0px;
  display: grid;
  grid-gap: 16px;
  max-height: 100vh;
  overflow-y: scroll;
`;

const Menu = () => {
  const { isTestingTransferStatus, setIsTestingTransferStatus } = useContext(AppContext);
  const navigate = useTypedNavigate();
  const location = useTypedLocation();
  const {
    state,
    toggleOneTimeCode,
    toggleRestart,
    toggleLocationState,
    setTransferInputReasonOverride,
    setTransferStatusOverride,
  } = useContext(DevelopmentEnvironmentContext);
  const [adminUsername, setAdminUsername] = useState(Session.adminUsername);
  const [adminPassword, setAdminPassword] = useState(Session.adminPassword);
  const [showZohoBooking, setShowZohoBooking] = useState<boolean>(false);
  const [urlSlug, setUrlSlug] = useState<string>("");

  const transferInputReasonOptions = [] as ReactSelectOptions;
  Object.values(TransferActions).forEach((value) => {
    transferInputReasonOptions.push({
      value,
      label: value,
    });
  });

  const transferStatusOptions = [] as ReactSelectOptions;
  Object.values(TransferStatusTypes).forEach((value) => {
    if (
      value === TransferStatusTypes.none ||
      value === TransferStatusTypes.mailProcess ||
      value === TransferStatusTypes.inProgress
    )
      return;
    transferStatusOptions.push({
      value,
      label: value,
    });
  });

  const checkQueueUI = () => {
    navigate(route(RoutePaths.transferActions), {
      state: {
        isTestingTransferAction: {
          transferInstructions: [
            new TransferInstruction({
              transferInstructionId: 123,
              transferId: 123,
              transferStatus: TransferStatusTypes.inQueue,
            }),
          ],
        },
      },
    });
  };

  useDidMountEffect(() => {
    Session.adminUsername = adminUsername;
  }, [adminUsername]);

  useDidMountEffect(() => {
    Session.adminPassword = adminPassword;
  }, [adminPassword]);

  const transferInstructionRef = useRef<HTMLInputElement | null>(null);

  const renderTransferActions = (num: string | number): Array<TransferInstruction> => {
    const numValue = typeof num === "number" ? num : parseInt(num, 10);
    if (numValue === 0) {
      return [];
    }
    if (numValue === 1) {
      return [new TransferInstruction()];
    }
    return [new TransferInstruction(), new TransferInstruction()];
  };

  const goToTransferActionsPage = () => {
    if (transferInstructionRef?.current?.value !== undefined) {
      navigate(route(RoutePaths.transferActions), {
        state: {
          isTestingTransferAction: {
            transferInstructions: renderTransferActions(transferInstructionRef?.current?.value),
          },
        },
      });
    }
  };

  const showIRAOptionsScreens = (iraType: string) => {
    navigate(route(RoutePaths.iraOptions), {
      state: {
        transferId: "-1",
        // transferOption: locationState.transferOption,
        accountType: iraType === "handsoff" ? DestinationTransferOptions.roboIra : DestinationTransferOptions.intoIra,
      },
    });
    window.location.reload();
  };

  const testZohoBooking = () => {
    const zohoBookingDiv = document.getElementById("zohoBookingDivTest");

    if (zohoBookingDiv) {
      zohoBookingDiv.innerHTML = "";
      const iframe = generateZohoBookingIframe({
        user: new User(),
        userDetails: new UserDetails(),
        transferInstructionId: "testing",
      });
      zohoBookingDiv.appendChild(iframe);
      setShowZohoBooking(true);
    }
  };

  const testCors = async () => {
    try {
      await axios.get("https://api.secure.usemanifest.com/manifest/config/employerCheck?urlName=aim");
    } catch (e) {
      Sentry.captureException(
        new Error(`Unable to fetch employer config: ${e instanceof Error ? e.message : String(e)}`)
      );
      console.error(e);
    }
  };

  const testUrl = async () => {
    if (!urlSlug) return;
    try {
      await axios.get(`https://portal.api.dev.usemanifest.com/authentication/validateSlug?slug=${urlSlug}`);
    } catch (e) {
      console.error(e);
    }
  };

  const onChangeUrlSlug = (e) => setUrlSlug(e);

  const checkHighfiveScreen = () => {
    return navigate(route(RoutePaths.userFeedback), {
      state: {
        transferId: "9999",
      },
    });
  };

  const onClickNewSignup = () => {
    window.localStorage.setItem(
      "signUp",
      JSON.stringify({
        partner: "Brose",
        participantType: "active",
      })
    );
    window.location.href = `https://us-east-1rqijyzjgc.auth.us-east-1.amazoncognito.com/signup?client_id=5bpe0d4i4sjdghos0qv3ltctl0&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(
      "https://dev.dev-manifest.com/redirect/auth"
    )}`;
  };

  const onClickNewLogin = () => {
    window.location.href = `https://us-east-1rqijyzjgc.auth.us-east-1.amazoncognito.com/login?client_id=5bpe0d4i4sjdghos0qv3ltctl0&response_type=code&scope=email+openid+phone+profile&redirect_uri=${encodeURIComponent(
      "https://dev.dev-manifest.com/redirect/auth"
    )}`;
  };

  const onClickSandbox = () => {
    navigate(RoutePaths.sandbox);
  };

  const onClickTransferAction = () => {
    navigate(RoutePaths.transferCheck, {
      state: {
        transferId: "31302",
        isIRAPartner: true,
        iraPartner: "Betterment",
      },
    });
    // window.location.replace(
    //   "http://localhost:3001/redirect/callback?partner=betterment&userId=935681c8-66ef-4976-a3ab-60fc574ec157&transferId=31262&iraAccountOpeningId=2425&trial=2"
    // );
    // navigate(route(RoutePaths.transferStatus, { action: "waitForIraOpening" }), {
    //   state: {
    //     transferInstructionId: "20814",
    //   },
    // });
  };

  const testAllTransferInstructionScenarios = () => {
    setIsTestingTransferStatus(!isTestingTransferStatus);
  };
  return (
    <MyTile id="dev-tool-menu">
      <ZohoBookingPopup
        handlePopup={() => setShowZohoBooking(false)}
        showPopup={showZohoBooking}
        content={<StyledCalendly id="zohoBookingDivTest" />}
      />
      <MenuContainer>
        <Button
          name="create"
          onClick={testAllTransferInstructionScenarios}
          inverted
          text="Test all post transfer scenarios"
        />
        <CheckboxMenuItem onClick={() => toggleOneTimeCode()} enabled={state.displayOneTimeCode}>
          Display one time code
        </CheckboxMenuItem>
        <CheckboxMenuItem onClick={() => toggleRestart()} enabled={state.displayRestartTransfer}>
          Show restart transfer
        </CheckboxMenuItem>

        <CheckboxMenuItem onClick={() => toggleLocationState()} enabled={state.showLocationState}>
          Show location state
        </CheckboxMenuItem>
        <Button
          onClick={() => showIRAOptionsScreens("handson")}
          text="Show IRA Options (Hands on)"
          name="showIRAscreen"
        />
        <Button
          onClick={() => showIRAOptionsScreens("handsoff")}
          text="Show IRA Options (Hands off)"
          name="showIRAscreen"
        />
        {isLocalEnvironment() && (
          <Button name="testBetterment" text="Test Betterment" onClick={onClickTransferAction} inverted />
        )}

        <Button name="testNewAuth" text="Test new signup" onClick={onClickNewSignup} inverted />
        <Button name="testNewAuth" text="Test new login" onClick={onClickNewLogin} inverted />
        <Button name="tetSandbox" text="Sandbox" onClick={onClickSandbox} inverted />
        <Button onClick={checkQueueUI} text="Check Queue UI" name="checkQueueUI" />
        <Input type="text" onChange={onChangeUrlSlug} value={urlSlug} name="changeUrlSlug" />
        <Button onClick={testUrl} text="Test Url" name="testUrl" />
        <Button onClick={checkHighfiveScreen} text="Check Highfive screen" name="checkHighFive" />

        <Button onClick={testZohoBooking} text="Test Zoho Booking" name="testZohoBooking" />
        <span>Go to transfer actions. Num of actions: </span>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input type="number" ref={transferInstructionRef} id="num-transfer-actions" defaultValue={0} />
          <button
            style={{
              width: "80px",
              background: ColorValues.mint,
              height: "40px",
              marginLeft: "10px",
              color: "white",
            }}
            onClick={goToTransferActionsPage}
            disabled={transferInstructionRef.current === undefined}
          >
            Go
          </button>
        </div>

        <Input
          name="adminUsername"
          type="text"
          title="Admin Username"
          value={adminUsername}
          onChange={setAdminUsername}
        />
        <Input
          name="adminPassword"
          type="password"
          title="Admin Password"
          value={adminPassword}
          onChange={setAdminPassword}
        />
        <Button
          name="create"
          onClick={testAllTransferInstructionScenarios}
          inverted
          text="Test all post transfer scenarios"
        />

        <Button name="testCors" onClick={testCors} inverted text="Test Cors" />
      </MenuContainer>
    </MyTile>
  );
};

export default DeveloperTools;
